<template>
  <div class="filters-root h-full flex flex-col relative">
    <div class="filters-head">
      <span class="filters-title">Filters</span>
      <span v-if="activeCount" class="filters-active">{{ activeCount }}</span>
      <button
        v-if="activeCount"
        type="button"
        class="filters-clear"
        @click="clearFilters"
      >
        Clear all
      </button>
    </div>
    <div class="relative">
      <FacetGroup
        v-for="name in orderedFacets"
        :key="name"
        :title="groupTitle(name)"
        :open="isOpen(name)"
        :selected="selectedCount(name)"
        :total="optionCount(name)"
        @toggle="toggleOpen(name)"
      >
        <CategorySelector
          v-if="name === CATEGORIES"
          :categories="facets['hierarchicalCategories.lvl0']"
          :subcategories="facets['hierarchicalCategories.lvl1']"
          :selection="value"
          @selected="selectedFacet"
          @removed="removedFacet"
        />
        <AvailabilitySelector
          v-else-if="name === AVAILABILITY"
          :facets="facets"
          :selection="value"
          @selected="selectedFacet"
          @removed="removedFacet"
        />
        <GenericFacetSelector
          v-else
          :type="name"
          :options="facets[name]"
          :selection="value"
          @selected="selectedFacet"
          @removed="removedFacet"
        />
      </FacetGroup>
      <div
        v-if="vehicleSelectionInProcess"
        class="w-full h-full absolute
        left-0 top-0 bg-black bg-opacity-60"
      ></div>
    </div>
    <!-- Sólo en móvil, donde los filtros tapan los resultados. -->
    <div v-if="!vehicleSelectionInProcess" class="mobile-bar">
      <button type="button" class="mobile-close" @click="$emit('close')">
        Show results
      </button>
      <button
        v-if="activeCount"
        type="button"
        class="mobile-clear"
        @click="clearFilters"
      >
        Clear all
      </button>
    </div>
  </div>
</template>
<script>
import AvailabilitySelector from "./AvailabilitySelector.vue";
import CategorySelector from "./CategorySelector.vue";
import FacetGroup from "./FacetGroup.vue";
import GenericFacetSelector from "./GenericFacetSelector.vue";
import { OFFER, facetTitle, isAvailabilityFacet } from "./facetLabels";

// El bloque de categorías y subcategorías (hierarchicalCategories.lvl0/lvl1)
// en FACET_ORDER.
const CATEGORIES = "hierarchicalCategories";
// La disponibilidad en cualquier tienda, en cada una y "On offer", juntas en
// un solo filtro (AvailabilitySelector).
const AVAILABILITY = "availability";

// Orden de los filtros en la barra lateral, de más a menos peso: primero los de
// esta lista, en este orden, y después los demás, en el orden en que llegan del
// buscador. Un nombre terminado en * vale para todos los que empiezan así
// (att_* son los atributos del producto).
const FACET_ORDER = ["brand", CATEGORIES, AVAILABILITY, "att_*"];

// Los que empiezan abiertos; los demás, plegados salvo que tengan algo puesto.
// Lo que abra o cierre cada usuario se recuerda en su navegador.
const OPEN_BY_DEFAULT = [AVAILABILITY];
const OPEN_STORAGE_KEY = "searchFilters.open";

// Facets que no se muestran como filtro: fitment es el vehículo (tiene su
// propio selector) y las categorías se pintan juntas en CATEGORIES.
const HIDDEN_FACETS = [
  "fitment",
  "fitmentNamed",
  "categories",
  "hierarchicalCategories.lvl0",
  "hierarchicalCategories.lvl1"
];

const CATEGORY_TYPES = [
  "hierarchicalCategories.lvl0",
  "hierarchicalCategories.lvl1"
];
// El vehículo no cuenta como filtro puesto ni se quita con "Clear all".
const VEHICLE_TYPES = ["fitment", "vehicle"];

const inAvailability = type => isAvailabilityFacet(type) || type === OFFER;

const weightOf = facet => {
  const index = FACET_ORDER.findIndex(entry =>
    entry.endsWith("*") ? facet.startsWith(entry.slice(0, -1)) : entry === facet
  );
  return index < 0 ? FACET_ORDER.length : index;
};

const readOpenState = () => {
  try {
    return JSON.parse(localStorage.getItem(OPEN_STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

export default {
  name: "searchFilters",
  components: {
    AvailabilitySelector,
    CategorySelector,
    FacetGroup,
    GenericFacetSelector
  },
  props: {
    facets: {
      type: Object,
      required: true
    },
    value: {
      type: Array,
      required: true
    },
    vehicleSelectionInProcess: {
      type: Boolean,
      required: false,
      default() {
        return false;
      }
    }
  },
  data() {
    return {
      CATEGORIES,
      AVAILABILITY,
      openState: readOpenState()
    };
  },
  computed: {
    orderedFacets() {
      const names = Object.keys(this.facets).filter(
        facet =>
          !HIDDEN_FACETS.includes(facet) &&
          !inAvailability(facet) &&
          Object.keys(this.facets[facet] || {}).length
      );
      if (
        this.facets["hierarchicalCategories.lvl0"] &&
        this.facets["hierarchicalCategories.lvl1"]
      ) {
        names.push(CATEGORIES);
      }
      if (Object.keys(this.facets).some(inAvailability)) {
        names.push(AVAILABILITY);
      }
      // sort es estable: a igual peso queda el orden en que llegaron.
      return names.sort((a, b) => weightOf(a) - weightOf(b));
    },
    activeCount() {
      return this.value.filter(facet => !VEHICLE_TYPES.includes(facet.type))
        .length;
    }
  },
  methods: {
    groupTitle(name) {
      if (name === CATEGORIES) return "Category";
      if (name === AVAILABILITY) return "Availability";
      return facetTitle(name);
    },
    selectedCount(name) {
      const belongs =
        name === CATEGORIES
          ? type => CATEGORY_TYPES.includes(type)
          : name === AVAILABILITY
          ? inAvailability
          : type => type === name;
      return this.value.filter(facet => belongs(facet.type)).length;
    },
    optionCount(name) {
      if (name === AVAILABILITY) return 0;
      const options =
        name === CATEGORIES
          ? this.facets["hierarchicalCategories.lvl0"]
          : this.facets[name];
      return Object.keys(options || {}).length;
    },
    isOpen(name) {
      if (name in this.openState) return this.openState[name];
      return OPEN_BY_DEFAULT.includes(name) || this.selectedCount(name) > 0;
    },
    toggleOpen(name) {
      this.openState = { ...this.openState, [name]: !this.isOpen(name) };
      try {
        localStorage.setItem(OPEN_STORAGE_KEY, JSON.stringify(this.openState));
      } catch (e) {
        // Sin almacenamiento (ventana privada) sólo dura hasta recargar.
      }
    },
    clearFilters() {
      // El vehículo se queda: el del selector (fitment) o el reconocido en lo
      // escrito en la pestaña Typesense (vehicle).
      this.$emit(
        "input",
        this.value.filter(facet => VEHICLE_TYPES.includes(facet.type))
      );
    },
    // Facetas que solo admiten un valor a la vez (p. ej. el vehículo/fitment).
    // El resto (categorías, subcategorías y facetas genéricas) admiten multi-select.
    isSingleSelectType(type) {
      return type === "fitment";
    },
    selectedFacet({ type, value, valueLabel }) {
      let currentFacetsSelection = [...this.value];
      if (this.isSingleSelectType(type)) {
        const found = currentFacetsSelection.findIndex(
          entry => entry.type == type
        );
        if (found >= 0) {
          currentFacetsSelection[found] = { type, value, valueLabel };
        } else {
          currentFacetsSelection.push({ type, value, valueLabel });
        }
      } else {
        const alreadySelected = currentFacetsSelection.some(
          entry => entry.type == type && entry.value == value
        );
        if (!alreadySelected) {
          currentFacetsSelection.push({ type, value, valueLabel });
        }
      }
      this.$emit("input", currentFacetsSelection);
    },
    removedFacet({ type, value }) {
      let currentFacetsSelection = [...this.value];
      if (this.isSingleSelectType(type) || value === undefined) {
        currentFacetsSelection = currentFacetsSelection.filter(
          entry => entry.type != type
        );
      } else {
        currentFacetsSelection = currentFacetsSelection.filter(
          entry => !(entry.type == type && entry.value == value)
        );
      }
      this.$emit("input", currentFacetsSelection);
    }
  }
};
</script>
<style lang="scss" scoped>
/* clip y no hidden: hidden vuelve al div contenedor de scroll y la barra de
   botones (sticky) deja de pegarse al borde inferior de la ventana. */
.filters-root {
  overflow-x: clip;
}

.filters-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 2px 10px;
}

.filters-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.filters-active {
  min-width: 20px;
  padding: 0 6px;
  border-radius: 9999px;
  background: #2563eb;
  color: white;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.filters-clear {
  margin-left: auto;
  font-size: 13px;
  font-weight: 500;
  color: #2563eb;

  &:hover {
    text-decoration: underline;
  }
}

.mobile-bar {
  display: none;
  position: sticky;
  bottom: 0;
  z-index: 10;
  gap: 8px;
  padding: 12px 0;
  background: linear-gradient(to top, white 75%, rgba(255, 255, 255, 0.7));

  button {
    flex: 1 1 0;
    height: 44px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
  }
}

.mobile-close {
  background: #111827;
  color: white;
}

.mobile-clear {
  border: 1px solid #d1d5db;
  background: white;
  color: #111827;
}

@media screen and (max-width: 1000px) {
  .mobile-bar {
    display: flex;
  }
}
</style>
